/**
 * Marketing Store — eigen marketing-platform (Supabase + Resend).
 *
 * Vervangt de read-only campaign-store: campagnes, journeys, segmenten,
 * contacten en instellingen zijn hier volledig beheerbaar via de
 * /api/marketing/*-endpoints (admin-only).
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── Types (spiegel van api/_lib/marketing/db.ts) ───────────────────────────

export type CampaignStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'cancelled';

export interface MarketingCampaign {
  id: string;
  key: string | null;
  name: string;
  brand: string;
  status: CampaignStatus;
  segment_id: string | null;
  template_id: string | null;
  subject_override: string | null;
  scheduled_at: string | null;
  audience_count: number | null;
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  sent_at: string | null;
  created_at: string;
}

export interface CampaignStats {
  sends: Record<string, number>;
  events: Record<string, number>;
  uniqueEvents: Record<string, number>;
}

export interface AudiencePreview {
  total: number;
  allowed: number;
  suppressed: Record<string, number>;
  sample: Array<{ id: string; email: string; first_name: string | null; last_name: string | null }>;
}

export interface MarketingSegment {
  id: string;
  key: string;
  name: string;
  brand: string;
  type: 'smart' | 'manual';
  definition: { all: Array<{ field: string; op: string; value?: unknown }> } | null;
  description: string | null;
  member_count: number | null;
  last_computed_at: string | null;
}

export interface MarketingTemplate {
  id: string;
  key: string;
  brand: string;
  name: string;
  subject: string;
  variables: Array<{ name: string; example?: string }>;
  version: number;
  source_path: string | null;
  updated_at: string;
}

export interface MarketingJourney {
  id: string;
  key: string;
  name: string;
  brand: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  trigger: { type: string; months?: number; days?: number; segment_key?: string };
  exit_conditions: string[];
  settings: Record<string, unknown>;
  created_at?: string;
  stateCounts?: Record<string, number>;
}

export interface MarketingJourneyStep {
  id: string;
  journey_id: string;
  step_order: number;
  name: string;
  wait_days: number;
  template_id: string | null;
  subject_override: string | null;
  marketing_templates?: { key: string; name: string; subject: string } | null;
}

export interface MarketingContact {
  id: string;
  sf_account_id: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  postcode: string | null;
  city: string | null;
  recency: string | null;
  order_count: number;
  last_service_date: string | null;
  blacklisted: boolean;
  open_complaint: boolean;
}

export interface DashboardData {
  contacts: { total: number; subscribed: number };
  sends30d: Record<string, number>;
  events30d: Record<string, number>;
  activeJourneys: Array<{ id: string; key: string; name: string; status: string }>;
  contactsInJourney: number;
  recentSends: Array<Record<string, unknown>>;
  lastSync: { at?: string; summary?: Record<string, unknown> } | null;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Niet ingelogd');
  return token;
}

export async function marketingFetch<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const token = await getToken();
  const res = await fetch(path, {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || `API-fout (${res.status})`);
  }
  return data as T;
}

// ─── Store ───────────────────────────────────────────────────────────────────

interface MarketingState {
  dashboard: DashboardData | null;
  campaigns: MarketingCampaign[];
  segments: MarketingSegment[];
  templates: MarketingTemplate[];
  journeys: MarketingJourney[];
  isLoading: boolean;
  error: string | null;

  fetchDashboard: () => Promise<void>;
  fetchCampaigns: () => Promise<void>;
  fetchSegments: () => Promise<void>;
  fetchTemplates: () => Promise<void>;
  fetchJourneys: () => Promise<void>;
}

export const useMarketingStore = create<MarketingState>()((set) => ({
  dashboard: null,
  campaigns: [],
  segments: [],
  templates: [],
  journeys: [],
  isLoading: false,
  error: null,

  fetchDashboard: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await marketingFetch<DashboardData>('/api/marketing/dashboard');
      set({ dashboard: data, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Fout bij laden' });
    }
  },

  fetchCampaigns: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await marketingFetch<{ campaigns: MarketingCampaign[] }>('/api/marketing/campaigns');
      set({ campaigns: data.campaigns, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Fout bij laden' });
    }
  },

  fetchSegments: async () => {
    try {
      const data = await marketingFetch<{ segments: MarketingSegment[] }>('/api/marketing/segments');
      set({ segments: data.segments });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Fout bij laden' });
    }
  },

  fetchTemplates: async () => {
    try {
      const data = await marketingFetch<{ templates: MarketingTemplate[] }>('/api/marketing/templates');
      set({ templates: data.templates });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Fout bij laden' });
    }
  },

  fetchJourneys: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await marketingFetch<{ journeys: MarketingJourney[] }>('/api/marketing/journeys');
      set({ journeys: data.journeys, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Fout bij laden' });
    }
  },
}));
